import dayjs from 'dayjs'
import useInterval from 'embed_v2/hooks/useInterval'
import { WIDGET_TYPES } from 'embed_v2/types/version'
import groupBy from 'lodash/groupBy'
import { useCallback, useContext, useEffect, useState } from 'preact/hooks'
import i18n from 'core/services/i18n'
import { ElementDatetimeOptions, Label } from 'core/types/element/element_datetime'
import optionsPropTypes from 'PropsSpec'
import WidgetContext from '../Context'
import Dropdown from './common/Dropdown'
import { OptionState } from './common/ReactDropdown'
import List from './datetime/List'
import type Session from 'embed_v2/types/session'

const generateTimeLeft = (scheduled_at?: string, running?: boolean) => {
  const date = !running && scheduled_at && dayjs().to(scheduled_at)
  return date ? `${i18n.t('layout.time.starts')} ${date}` : i18n.t('layout.time.starts_immediately')
}

const formatOptionLabel = (
  { label, scheduled_at, running, isSelected, isFocused }: FormattedSession & OptionState,
  options: ElementDatetimeOptions['dropdown']['menu']
) => {
  const extractStyles = (opts: Label) => ({
    color: isSelected || isFocused ? `rgba(${options.selected_text_color})` : `rgba(${opts.text_color})`,
    lineHeight: opts.line_height,
    textTransform: opts.text_transform,
    textDecoration: opts.text_decoration,
    letterSpacing: opts.letter_spacing,
    textShadow: opts.text_shadow,
    fontFamily: opts.font_family,
    fontSize: opts.font_size,
    fontWeight: opts.font_weight
  })

  const labelStyles = extractStyles(options.label)
  const sublabelStyles = extractStyles(options.sublabel)

  return (
    <div
      className="wf_reset wf_datetime_dropdown_option"
      style={{ display: 'flex', flexDirection: 'column', textAlign: options.text_align }}
    >
      <div style={labelStyles} className="wf_reset">
        {label}
      </div>
      <div style={sublabelStyles} className="wf_reset">
        {generateTimeLeft(scheduled_at, running)}
      </div>
    </div>
  )
}

type FormattedSession = {
  label: string
  id: number
  value: string
  scheduled_at: string
  expires_at?: string
  running: boolean
}

const beforeStart = (s: FormattedSession) => !s.scheduled_at || dayjs(s.scheduled_at).diff(dayjs()) > 0

const formatSessionValue = (s: Session): string => `${s.id}-${s.scheduled_at}`
export const formatSession = (s: Session): FormattedSession => ({
  label: s.formatted_scheduled_at,
  id: s.id,
  value: formatSessionValue(s),
  scheduled_at: s.scheduled_at,
  expires_at: s.expires_at,
  running: s.running
})

type Props = {
  options: ElementDatetimeOptions
}

const Datetime = (props: Props) => {
  const { options } = props
  const {
    version,
    sessions: widgetSessions,
    session: widgetSession,
    currentStep,
    redirectToJoinUrl,
    onSessionChange,
    isSessionValid,
    thankYouData
  } = useContext(WidgetContext)

  const emptySession: FormattedSession = {
    label: i18n.t('datetime.empty_label'),
    id: 0,
    value: 'N/A',
    scheduled_at: '',
    running: false
  }

  const placeholder = i18n.t('datetime.no_sessions')

  const session =
    version.widget_type === WIDGET_TYPES.THANK_YOU
      ? thankYouData
        ? formatSession(thankYouData.session)
        : emptySession
      : widgetSession
        ? formatSession(widgetSession)
        : null

  const formattedSessions = widgetSessions.map(formatSession)
  const getValidSessions = () => formattedSessions.filter(isSessionValid)

  const [sessions, setSessions] = useState(getValidSessions())

  const updateValidSession = () => {
    const validSessions = getValidSessions()
    setSessions(validSessions)

    if (session && !validSessions.map(s => s.value).includes(session.value)) {
      const firstValidSession = validSessions[0]
      const ws = widgetSessions.find(s => formatSessionValue(s) === firstValidSession.value)
      if (ws) {
        onSessionChange(ws)
      }
    }
  }

  // reset session when session expires
  const clear = useInterval(() => {
    clear()
    if (!session) return

    if (version.widget_type === WIDGET_TYPES.THANK_YOU) {
      !beforeStart(session) && redirectToJoinUrl()
    } else {
      updateValidSession()
    }
  }, 5000)

  useEffect(() => clear, [session])

  const onChange = useCallback((val?: string) => {
    if (val) {
      const ws = widgetSessions.find(s => formatSessionValue(s) === val)
      if (ws) {
        onSessionChange(ws)
      }
    }
  }, [])

  const groupHeading = options.dropdown.menu.group_heading
  const shouldGroup = !!groupHeading && sessions.some(s => s.running)

  const getGroups = () => Object.entries(groupBy(sessions, o => (o.running ? groupHeading.current : groupHeading.next)))

  const renderList = () => {
    const formatOption = (s: FormattedSession) => ({
      label: s.label,
      value: s.value,
      sublabel: generateTimeLeft(s.scheduled_at, s.running),
      running: s.running
    })

    const formatOptions = () => {
      if (shouldGroup) {
        return getGroups().map(([label, opts]) => ({ label, options: opts.map(formatOption) }))
      }

      return sessions.map(formatOption)
    }

    return (
      <List
        options={formatOptions()}
        value={session?.value}
        onChange={onChange}
        styles={options.list}
        readOnly={currentStep.success}
        placeholder={placeholder}
      />
    )
  }

  const renderDropdown = () => {
    const formatOptions = () => {
      if (shouldGroup) {
        return getGroups().map(([label, opts]) => ({ label, options: opts }))
      }

      return sessions
    }

    return (
      <Dropdown
        formatOptionLabel={(data: FormattedSession & OptionState) => formatOptionLabel(data, options.dropdown.menu)}
        styles={options.dropdown}
        value={session}
        options={formatOptions()}
        onChange={value => onChange(value.value)}
        readOnly={currentStep.success}
        placeholder={placeholder}
      />
    )
  }

  const renderUnselectable = () => {
    const styles = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: 50,
      border: `${options.unselectable.border_size}px ${options.unselectable.border_style} rgba(${options.unselectable.border_color})`,
      borderTopLeftRadius: options.unselectable.border_top_left,
      borderTopRightRadius: options.unselectable.border_top_right,
      borderBottomRightRadius: options.unselectable.border_bottom_left,
      borderBottomLeftRadius: options.unselectable.border_bottom_right,
      backgroundColor: `rgba(${options.unselectable.bg_color})`,
      color: `rgba(${options.unselectable.text_color})`,
      lineHeight: options.unselectable.line_height,
      textTransform: options.unselectable.text_transform,
      textDecoration: options.unselectable.text_decoration,
      letterSpacing: options.unselectable.letter_spacing,
      textShadow: options.unselectable.text_shadow,
      fontFamily: options.unselectable.font_family,
      fontSize: options.unselectable.font_size,
      fontWeight: options.unselectable.font_weight
    }

    return (
      <div className="wf_datetime wf_select_wrapper wf_reset" style={styles} readOnly={currentStep.success}>
        {session?.label}
      </div>
    )
  }

  const renderSelect = () => {
    switch (options.type) {
      case 'list':
        return renderList()
      case 'dropdown':
        return renderDropdown()
      case 'unselectable':
        return renderUnselectable()
      default:
        return null
    }
  }

  const styles = {
    width: '100%',
    height: '100%',
    paddingTop: options.padding_top,
    paddingRight: options.padding_right,
    paddingBottom: options.padding_bottom,
    paddingLeft: options.padding_left,
    boxSizing: 'border-box'
  }

  if (options.type === 'hidden') return null

  return (
    <div className="wf_datetime wf_reset" style={styles}>
      {renderSelect()}
    </div>
  )
}

Datetime.propTypes = {
  options: optionsPropTypes('datetime')
}

export default Datetime
